/**
 * TaskItem - A single task row component for the task list.
 *
 * Renders a task with checkbox, title, priority badge, due date, and tags.
 * Supports completion animation (300ms spring, strikethrough title).
 * Designed to work inside react-window's FixedSizeList for virtualized rendering.
 *
 * @see Requirements 2.4, 2.6, 2.7, 13.6
 */

import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import type { Task, Priority } from '@/types';
import { Badge } from '@/components/ui/Badge';

export interface TaskItemProps {
  /** The task data to render. */
  task: Task;
  /** Called when the entire task row is clicked (opens detail panel). */
  onClick: () => void;
  /** Called when the completion checkbox is clicked. */
  onComplete: () => void;
  /** Style object from react-window for absolute positioning. */
  style?: CSSProperties;
}

// Priority color mapping — uses design system tokens
const PRIORITY_COLORS: Record<Priority, string> = {
  none: '',
  low: 'var(--color-priority-low)',
  medium: 'var(--color-priority-medium)',
  high: 'var(--color-priority-high)',
};

const PRIORITY_LABELS: Record<Priority, string> = {
  none: '',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/**
 * Formats a due date string for display.
 * Returns "Today", "Tomorrow", relative day name, or formatted date.
 * Overdue tasks get a special indicator.
 */
function formatDueDate(dueDate: string): { text: string; isOverdue: boolean } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  due.setHours(0, 0, 0, 0);

  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { text: `Overdue`, isOverdue: true };
  }
  if (diffDays === 0) {
    return { text: 'Today', isOverdue: false };
  }
  if (diffDays === 1) {
    return { text: 'Tomorrow', isOverdue: false };
  }
  // Format as "Mon, Jan 15"
  const formatted = due.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return { text: formatted, isOverdue: false };
}

export function TaskItem({ task, onClick, onComplete, style }: TaskItemProps) {
  const [animating, setAnimating] = useState(false);
  const prevCompleted = useRef(task.isCompleted);

  // Detect when isCompleted transitions from false to true for animation
  useEffect(() => {
    if (task.isCompleted && !prevCompleted.current) {
      setAnimating(true);
      const timeout = setTimeout(() => setAnimating(false), 300);
      return () => clearTimeout(timeout);
    }
    prevCompleted.current = task.isCompleted;
  }, [task.isCompleted]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onComplete();
    },
    [onComplete]
  );

  const handleCheckboxKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCheckboxClick(e);
      }
    },
    [handleCheckboxClick]
  );

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const dueInfo = task.dueDate ? formatDueDate(task.dueDate) : null;
  const visibleTags = task.tags.slice(0, 3);
  const remainingTags = task.tags.length - 3;

  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        'flex items-center gap-3 px-4 cursor-pointer',
        'border-b border-border',
        'hover:bg-hover-bg',
        'transition-all duration-default ease-ease-default',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset',
        animating ? 'task-item-completing' : '',
      ].join(' ')}
      style={{
        ...style,
        height: style?.height ?? 56,
      }}
      onClick={onClick}
      onKeyDown={handleRowKeyDown}
      aria-label={`Task: ${task.title}${task.isCompleted ? ', completed' : ''}`}
    >
      {/* Checkbox */}
      <div
        role="checkbox"
        tabIndex={0}
        aria-checked={task.isCompleted}
        aria-label={`Mark "${task.title}" as ${task.isCompleted ? 'incomplete' : 'complete'}`}
        className={[
          'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center',
          'transition-all duration-default ease-ease-default',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
          task.isCompleted
            ? 'bg-primary border-primary'
            : 'border-border hover:border-primary',
        ].join(' ')}
        onClick={handleCheckboxClick}
        onKeyDown={handleCheckboxKeyDown}
      >
        {task.isCompleted && (
          <svg
            className="w-3 h-3 text-white"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 6L5 9L10 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Title + metadata */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span
          className={[
            'text-body font-normal truncate',
            'transition-all duration-default ease-ease-default',
            task.isCompleted ? 'line-through opacity-50 text-text-secondary' : 'text-text-primary',
          ].join(' ')}
        >
          {task.title}
        </span>

        {/* Tag dots */}
        {visibleTags.length > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0" aria-hidden="true">
            {visibleTags.map((tagId, i) => (
              <span
                key={tagId}
                className="w-2 h-2 rounded-full bg-primary"
                style={{ opacity: 1 - i * 0.2 }}
              />
            ))}
            {remainingTags > 0 && (
              <span className="text-xs text-text-secondary">+{remainingTags}</span>
            )}
          </div>
        )}
      </div>

      {/* Priority badge */}
      {task.priority !== 'none' && (
        <Badge
          text={PRIORITY_LABELS[task.priority]}
          color={PRIORITY_COLORS[task.priority]}
          size="sm"
          className="flex-shrink-0"
        />
      )}

      {/* Due date */}
      {dueInfo && (
        <span
          className={[
            'text-sm flex-shrink-0 whitespace-nowrap',
            dueInfo.isOverdue && !task.isCompleted
              ? 'text-high font-medium'
              : 'text-text-secondary',
          ].join(' ')}
          style={
            dueInfo.isOverdue && !task.isCompleted
              ? { color: 'var(--color-priority-high)' }
              : undefined
          }
        >
          {dueInfo.text}
        </span>
      )}
    </div>
  );
}
